import {
  Box,
  FormControl,
  InputLabel,
  OutlinedInput,
  Typography,
  Button,
  CircularProgress,
} from "@mui/material";
import React, { useEffect, useState, useRef } from "react";
import Lottie from "react-lottie";
import mfaAnim from "../../../../assets/gifs/mfa.json";
import { lottieLoopOptions } from "../../../../utils/Utils";
import AppButton from "../../../base/AppButton.component";
import VerificationLayout from "../../../layout/VerificationLayout.component";
import { useNavigate } from "react-router-dom";
import { useVerification } from "../../../../context/Verification.context";
import { navigateContinueDelivery, getContinueDeliveryNavTarget } from "../../../../utils/videoPlayer";

const RESEND_COOLDOWN = 1 * 15;



const SecurityCode = ({
  selectedOption,
  mfaSubmitHandler,
  securityCode,
  setSecurityCode,
  mfaSelectedHandler,
  status,
  onResendCode,
  isResending,
  submitError,
  onSubmitErrorHandled,
}) => {
  lottieLoopOptions.animationData = mfaAnim;
  const [disableButton, setDisableButton] = useState(false);
  const [resendCount, setResendCount] = useState(0);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  const [resendCooldown, setResendCooldown] = useState(RESEND_COOLDOWN);
  const [isTimerActive, setIsTimerActive] = useState(true);
  const timerRef = useRef(null);

  const navigate = useNavigate();
  const verification = useVerification();

  const startResendTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    setResendCooldown(RESEND_COOLDOWN);
    setIsTimerActive(true);
    timerRef.current = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          setIsTimerActive(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const formatCountdown = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  };

  useEffect(() => {
    startResendTimer();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  useEffect(() => {
    if (status === "PULLING_DATA") setDisableButton(false);
  }, [status]);

  useEffect(() => {
    if (submitError) {
      setDisableButton(false);
      onSubmitErrorHandled();
    }
  }, [submitError]);

  useEffect(() => {
    if (showSuccessMessage) {
      const timer = setTimeout(() => setShowSuccessMessage(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [showSuccessMessage]);

  const handleResendCode = async () => {
    const resendFn = onResendCode && selectedOption?.key ? onResendCode : mfaSelectedHandler;
    if (!resendFn) return;
    try {
      await resendFn();
      setResendCount((prev) => prev + 1);
      setSecurityCode("");
      setShowSuccessMessage(true);
      startResendTimer();
    } catch (error) {
      console.error("Resend failed:", error);
    }
  };
  // Continue Delivery → quote/thankYou first, then KW + artifacts if needed
  const handleContinueWithoutVerifying = () => {
    navigateContinueDelivery(
      navigate,
      verification,
      getContinueDeliveryNavTarget(verification),
    );
  };

  const getContactMethod = () => {
    if (!selectedOption?.key) return "";
    if (selectedOption.key.includes("email")) return "email";
    if (selectedOption.key.includes("sms") || selectedOption.key.includes("voice")) return "phone";
    if (selectedOption.key.includes("security")) return "security question";
    return selectedOption.key;
  };

  const isInputDisabled = status === "IDENTITY_VERIFICATION_OPTIONS" || status === "PROCESSING";

  const renderStatus = (status) => {
    if (status === "IDENTITY_VERIFICATION") return "Code sent! Please check your device and enter the code above.";
    if (status === "IDENTITY_VERIFICATION_OPTIONS") return "Selecting verification method...";
    if (status === "PROCESSING") return "Processing your request...";
    if (status === "PULLING_DATA") return "Verifying your code...";
    return `Status: ${status}`;
  };

  const isResendDisabled = isTimerActive || isResending || disableButton || !selectedOption;

  return (
    <VerificationLayout>
      <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", gap: 2 }}>
        <Typography variant="h6" align="center">Enter your security code</Typography>

        <Lottie
          options={lottieLoopOptions}
          height={window.innerWidth <= 750 ? 180 : 220}
          width={window.innerWidth <= 750 ? 150 : 190}
        />

        <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", gap: 2, width: "100%" }}>
          <Typography variant="body2" align="center" style={{ marginTop: "10px", whiteSpace: "pre-line" }}>
            {selectedOption?.message || "Enter your verification code"}
    
          </Typography>

          <FormControl sx={{ m: 1, marginTop: "10px", width: "100%", maxWidth: "400px" }} fullWidth variant="outlined">
            <InputLabel sx={{ color: isInputDisabled ? "#9e9e9e" : undefined, "&.Mui-focused": { color: isInputDisabled ? "#9e9e9e" : undefined } }}>
              {selectedOption?.key === "SECURITY_QUESTION" ? "Your answer" : "Security Code"}
            </InputLabel>
            <OutlinedInput
              label={selectedOption?.key === "SECURITY_QUESTION" ? "Your answer" : "Security Code"}
              value={securityCode}
              onChange={(event) => setSecurityCode(event.target.value)}
              disabled={isInputDisabled}
              sx={{ borderRadius: 2, border: "none" }}
              autoFocus
            />
          </FormControl>

          {disableButton && (
            <Box sx={{ width: "100%", display: "flex", justifyContent: "center", alignItems: "center", position: "relative", height: 60 }}>
              <CircularProgress color="primary" size={48} thickness={4} />
            </Box>
          )}

          <AppButton
            buttonText="Submit"
            loading={disableButton}
            clickHandler={(e) => {
              if (disableButton) { e.preventDefault(); return; }
              mfaSubmitHandler();
            }}
            sx={{ width: window.innerWidth <= 750 ? "80%" : "400px", display: disableButton ? "none" : "block" }}
            disabled={!securityCode || disableButton || isResending}
          />

          <Typography variant="body2" align="center" sx={{ marginTop: "8px", fontSize: "0.875rem" }}>
            Didn't get your code yet?
          </Typography>

<Button
  variant="outlined"
  sx={{
    textTransform: "none",
    fontSize: "0.875rem",
    color: isTimerActive ? "#555" : "primary.main",
    fontWeight: 700,
    borderColor: isTimerActive ? "#555" : "primary.main",
    borderWidth: "2px",
    minWidth: 160,
    "&:disabled": {
      opacity: 1, // keep full visibility
      color: "#555",
      borderColor: "#555",
      borderWidth: "2px",
    },
  }}
  onClick={handleResendCode}
  disabled={isResendDisabled}
  startIcon={isResending ? <CircularProgress size={16} /> : null}
>
  {isResending
    ? "Sending..."
    : isTimerActive
    ? `Resend Code (${formatCountdown(resendCooldown)})`
    : "Resend Code"}
</Button>

          <Typography
            variant="caption"
            align="center"
            sx={{
              fontSize: "0.75rem",
              color: status === "IDENTITY_VERIFICATION" ? "success.main" : "text.secondary",
              minHeight: "20px",
              whiteSpace: "pre-line",
            }}
          >
            {renderStatus(status)}
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", width: "100%" }}>
            <Typography variant="body2" sx={{ fontSize: "0.875rem", color: "text.secondary" }}>
              Having trouble receiving a code?
            </Typography>
            {/* 👇 Updated click handler */}
            <Typography
              variant="body2"
              onClick={handleContinueWithoutVerifying}
              sx={{
                color: "primary.main",
                fontSize: "0.875rem",
                fontWeight: 500,
                cursor: "pointer",
                "&:hover": { opacity: 0.8, textDecoration: "underline" },
              }}
            >
              Continue without verifying insurance
            </Typography>
          </Box>
        </Box>
      </Box>
    </VerificationLayout>
  );
};

export default SecurityCode;